import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type VendorArticleFormSchema, vendorArticleFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";
import { VendorArticleForm } from "@/forms/vendor-article-form";
import { client } from "@/hono";

/** Props Interface */
interface EditVendorArticleModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	articleId: string;
	vendorArticleId: string;
}

export function EditVendorArticleModal({
	onClose,
	isOpen,
	onComplete,
	articleId,
	vendorArticleId,
}: EditVendorArticleModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock.articles[":articleId"]["vendor-articles"][
			":vendorArticleId"
		].$get({
			param: { articleId, vendorArticleId },
		});
		const data = await responseHandler(response);
		return vendorArticleFormSchema.parse(data.vendorArticle);
	});
	const methods = useForm({
		resolver: resolver(vendorArticleFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: VendorArticleFormSchema) {
		const response = await client.stock.articles[":articleId"]["vendor-articles"][
			":vendorArticleId"
		].$patch({
			param: { articleId, vendorArticleId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.edit_vendor_article")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<VendorArticleForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
